import React, { useState } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  People as PeopleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CloudSync as CloudSyncIcon,
  Calculate as CalculateIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

import UserManagement from "./UserManagement";
import RoleManagement from "./RoleManagement";
import PrefinanceLegacyMigration from "./PrefinanceLegacyMigration";
import PrefinanceHistoricalRecalculation from "./PrefinanceHistoricalRecalculation";
import PrefinanceBankSaldoImport from "./PrefinanceBankSaldoImport";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const SidebarContainer = styled(Box)(({ theme }) => ({
  height: "fit-content",
  position: "sticky",
  top: theme.spacing(2),
}));

const Admin = () => {
  const { hasPermission } = usePermissions();
  const canMigrateLegacy = hasPermission(CAPABILITIES.adminMigrateLegacy, "X");
  const [currentView, setCurrentView] = useState("users");

  const menuItems = [
    {
      title: "User Management",
      icon: PeopleIcon,
      view: "users",
    },
    {
      title: "Role Management",
      icon: AdminPanelSettingsIcon,
      view: "roles",
    },
    ...(canMigrateLegacy
      ? [
          {
            title: "Prefinance migration",
            icon: CloudSyncIcon,
            view: "prefinanceMigration",
          },
          {
            title: "Prefinance recalculation",
            icon: CalculateIcon,
            view: "prefinanceRecalculation",
          },
          {
            title: "Prefinance saldo",
            icon: AccountBalanceIcon,
            view: "prefinanceSaldo",
          },
        ]
      : []),
  ];

  const handleMenuClick = (item) => {
    setCurrentView(item.view);
  };

  const isActive = (view) => {
    return currentView === view;
  };

  const renderContent = () => {
    switch (currentView) {
      case "users":
        return <UserManagement />;
      case "roles":
        return <RoleManagement />;
      case "prefinanceMigration":
        return <PrefinanceLegacyMigration />;
      case "prefinanceRecalculation":
        return <PrefinanceHistoricalRecalculation />;
      case "prefinanceSaldo":
        return <PrefinanceBankSaldoImport />;
      default:
        return (
          <Box>
            <Typography variant="h4">User Management</Typography>
            <Typography variant="body1">This is the default User Management page.</Typography>
          </Box>
        );
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Admin Panel" />

      <Typography variant="h3" gutterBottom>
        Admin Panel
      </Typography>

      <Grid container spacing={6}>
        {/* Left Sidebar Menu */}
        <Grid item xs={12} md={3}>
          <SidebarContainer>
            <Card>
              <CardContent p={0}>
                <List component="nav" disablePadding>
                  {menuItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItemButton
                        onClick={() => handleMenuClick(item)}
                        selected={isActive(item.view)}
                        sx={{
                          py: 2,
                          px: 3,
                          "&.Mui-selected": {
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                            "& .MuiListItemIcon-root": {
                              color: "primary.contrastText",
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                      </ListItemButton>
                      {index < menuItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </SidebarContainer>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          <Box>{renderContent()}</Box>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Admin;
